import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message, Progress, Button } from 'antd';
import { FolderOutlined, DownloadOutlined, EditOutlined, CheckCircleOutlined, CloseOutlined } from '@ant-design/icons';
import {
    useGetPortalProjectQuery,
    useGetDataGroupsQuery,
    useGetDataSourcesQuery,
    useUpdateStageStatusMutation,
    useDeleteDataGroupMutation,
} from '../../redux/api/compassPortal';
import { useGetIfUserIsAdminQuery, useGetUserTypeQuery } from '../../redux/api/user';
import StatusSelector from './StatusSelector';
import DataSourceTable from './DataSourceTable';
import DataGroupModal from './modals/DataGroupModal';
import DataSourceModal from './modals/DataSourceModal';
import DataSourceInfoModal from './modals/DataSourceInfoModal';
import ProcessingIssueModal from './modals/ProcessingIssueModal';

const DataCollectionPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [showSourceModal, setShowSourceModal] = useState(false);
    const [editingSource, setEditingSource] = useState(null);
    const [viewingSource, setViewingSource] = useState(null);
    const [issueSource, setIssueSource] = useState(null);

    const { data: project, isLoading: projectLoading } = useGetPortalProjectQuery({ projectId });
    const { data: groups } = useGetDataGroupsQuery({ projectId });
    const { data: sources } = useGetDataSourcesQuery({ projectId });
    const { data: isAdmin } = useGetIfUserIsAdminQuery();
    const { data: userType } = useGetUserTypeQuery();
    const isInternal = userType === 'Internal' || userType === 'Admin';
    const [updateStageStatus] = useUpdateStageStatusMutation();
    const [deleteGroup] = useDeleteDataGroupMutation();

    const stage = project?.stages?.find((s) => s.id === 'data-collection');

    const getSourceCount = (groupId) => (sources || []).filter((s) => s.group_id === groupId).length;

    const groupMap = {};
    (groups || []).forEach((g) => { groupMap[g.group_id] = g.name; });

    // Progress stats
    const progressStats = useMemo(() => {
        const all = sources || [];
        const filtered = selectedGroupId ? all.filter((s) => s.group_id === selectedGroupId) : all;
        const collected = filtered.filter((s) => s.collected).length;
        const total = filtered.length;
        const percent = total > 0 ? Math.round((collected / total) * 100) : 0;
        return { collected, total, percent };
    }, [sources, selectedGroupId]);

    const selectedGroupName = selectedGroupId ? groupMap[selectedGroupId] : null;

    const handleStatusChange = async (status) => {
        if (status === 'complete') {
            const allCollected = sources?.length > 0 && sources.every((s) => s.collected);
            if (!allCollected) {
                message.warning('All sources must be collected before completing this stage');
                return;
            }
        }
        await updateStageStatus({ projectId, stage_id: 'data-collection', status }).unwrap();
    };

    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    if (projectLoading) {
        return <div style={{ width: '100%', textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>;
    }

    const hasGroups = groups && groups.length > 0;
    const hasSources = sources && sources.length > 0;

    return (
        <div style={{ width: '100%' }}>
            <button className="cp-back-link" onClick={() => navigate(`/compass-portal/${projectId}`)}>
                &larr; Back to timeline
            </button>

            <div className="cp-stage-header">
                <div className="cp-stage-header__left">
                    <h2>Data Collection</h2>
                </div>
                <div className="cp-stage-header__center">
                    <div className="cp-stage-header__dateline">
                        <span className="cp-stage-header__dateline-label">DEADLINE</span>
                        <span className="cp-stage-header__dateline-value">{stage?.due_date || '—'}</span>
                    </div>
                    <div className="cp-stage-header__dateline cp-stage-header__dateline--today">
                        <span className="cp-stage-header__dateline-label">TODAY</span>
                        <span className="cp-stage-header__dateline-value">{today}</span>
                    </div>
                </div>
                {isAdmin && (
                    <StatusSelector
                        currentStatus={stage?.status}
                        onChange={handleStatusChange}
                    />
                )}
            </div>

            <div className="cp-body">
                <div className="cp-sidebar">
                    <div className="cp-sidebar__title">Data Groups</div>
                    {!hasGroups && (
                        <div className="cp-empty-state cp-empty-state--small">
                            <p>No data groups yet</p>
                            <Button size="small" type="dashed" icon={<FolderOutlined />} onClick={() => { setEditingGroup(null); setShowGroupModal(true); }}>
                                Create group
                            </Button>
                        </div>
                    )}
                    {(groups || []).map((group) => (
                        <div
                            key={group.group_id}
                            className={`cp-sidebar__item ${selectedGroupId === group.group_id ? 'cp-sidebar__item--active' : ''}`}
                            onClick={() => setSelectedGroupId(selectedGroupId === group.group_id ? null : group.group_id)}
                        >
                            <span className="cp-sidebar__item-name">{group.name}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <EditOutlined
                                    style={{ fontSize: 12, color: 'var(--medium-light-grey-color)', cursor: 'pointer' }}
                                    onClick={(e) => { e.stopPropagation(); setEditingGroup(group); setShowGroupModal(true); }}
                                />
                                <span className="cp-sidebar__item-count">{getSourceCount(group.group_id)}</span>
                            </span>
                        </div>
                    ))}
                </div>

                <div className="cp-main">
                    <div className="cp-main__title-row">
                        <h3 className="cp-main__title">Data Overview</h3>
                    </div>

                    {/* Progress bar + status messaging */}
                    {hasSources && progressStats.percent === 100 && (
                        <div className="cp-status-banner cp-status-banner--success">
                            <div><CheckCircleOutlined style={{ marginRight: 8 }} />All data has been collected. Our team will now begin processing your data.</div>
                            <Button type="link" onClick={() => navigate(`/compass-portal/${projectId}/data-processing`)} style={{ padding: 0, color: 'var(--green-color)' }}>
                                Go to Data Processing &rarr;
                            </Button>
                        </div>
                    )}
                    {hasSources && progressStats.percent < 100 && (
                        <div className="cp-progress-bar">
                            <div className="cp-progress-bar__info">
                                <span>{progressStats.collected} / {progressStats.total} sources collected</span>
                            </div>
                            <Progress
                                percent={progressStats.percent}
                                strokeColor="var(--green-color)"
                                trailColor="rgba(255,255,255,0.08)"
                                showInfo={false}
                                size="small"
                            />
                            <div style={{ fontSize: 12, color: 'var(--medium-light-grey-color)', marginTop: 6 }}>
                                Upload files and mark each source as collected. Once all sources are collected, processing will begin.
                            </div>
                        </div>
                    )}

                    {/* Filter indicator */}
                    {selectedGroupName && (
                        <div className="cp-filter-indicator">
                            Showing sources from: <strong>{selectedGroupName}</strong>
                            <button className="cp-filter-indicator__clear" onClick={() => setSelectedGroupId(null)}>
                                <CloseOutlined /> Show all
                            </button>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                        <button className="cp-action-btn" onClick={() => { setEditingGroup(null); setShowGroupModal(true); }}>
                            <FolderOutlined /> Create New Data Group
                        </button>
                        <button className="cp-action-btn" onClick={() => { setEditingSource(null); setShowSourceModal(true); }}>
                            <DownloadOutlined /> Create New Data Source
                        </button>
                    </div>

                    {/* Empty state */}
                    {hasGroups && !hasSources && (
                        <div className="cp-empty-state">
                            <p>No data sources yet</p>
                            <p style={{ color: 'var(--medium-light-grey-color)', fontSize: 13 }}>
                                Create a data source to start collecting data for this project.
                            </p>
                            <Button type="primary" icon={<DownloadOutlined />} onClick={() => { setEditingSource(null); setShowSourceModal(true); }}>
                                Create New Data Source
                            </Button>
                        </div>
                    )}

                    {!hasGroups && !hasSources && (
                        <div className="cp-empty-state">
                            <p>Get started by creating a data group</p>
                            <p style={{ color: 'var(--medium-light-grey-color)', fontSize: 13 }}>
                                Data groups organise your sources by category (e.g. Paid Digital, Organic, CRM).
                            </p>
                            <Button type="primary" icon={<FolderOutlined />} onClick={() => { setEditingGroup(null); setShowGroupModal(true); }}>
                                Create First Data Group
                            </Button>
                        </div>
                    )}

                    {hasSources && (
                        <DataSourceTable
                            sources={sources}
                            groups={groups}
                            projectId={projectId}
                            mode="collection"
                            selectedGroupId={selectedGroupId}
                            onEditSource={(source) => { setEditingSource(source); setShowSourceModal(true); }}
                            onViewSource={setViewingSource}
                            onRaiseIssue={setIssueSource}
                            isAdmin={isAdmin}
                            isInternal={isInternal}
                        />
                    )}
                </div>
            </div>

            <DataGroupModal open={showGroupModal} onClose={() => { setShowGroupModal(false); setEditingGroup(null); }} projectId={projectId} editingGroup={editingGroup} />
            <DataSourceModal
                open={showSourceModal}
                onClose={() => { setShowSourceModal(false); setEditingSource(null); }}
                projectId={projectId}
                groups={groups}
                editingSource={editingSource}
                metadataSchema={project?.metadata_schema || []}
            />
            <DataSourceInfoModal
                open={!!viewingSource}
                onClose={() => setViewingSource(null)}
                source={viewingSource}
                projectId={projectId}
                groupName={viewingSource ? groupMap[viewingSource.group_id] : ''}
                metadataSchema={project?.metadata_schema || []}
            />
            <ProcessingIssueModal
                open={!!issueSource}
                onClose={() => setIssueSource(null)}
                source={issueSource}
                projectId={projectId}
                isAdmin={isAdmin}
                isInternal={isInternal}
            />
        </div>
    );
};

export default DataCollectionPage;
